import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect } from "react";
import {
  Bold, Italic, Underline, Strikethrough,
  Heading2, Heading3,
  List, ListOrdered, Quote,
  Link as LinkIcon, Undo2, Redo2, Pilcrow,
} from "lucide-react";

/**
 * TipTap-based WYSIWYG editor for the admin CMS.
 * Stores output as HTML (via editor.getHTML()) so the public renderer can
 * emit it directly with dangerouslySetInnerHTML. The public renderer also
 * still supports legacy markdown-lite content for older posts.
 */
export default function RichTextEditor({ value, onChange, placeholder = "Write here…", testId = "rich-editor" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // starter-kit already includes bold, italic, strike, bulletList,
        // orderedList, blockquote, paragraph, history, hardBreak, listItem, etc.
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "tiptap-content prose-law max-w-none min-h-[320px] px-4 py-4 focus:outline-none",
        "data-testid": `${testId}-surface`,
      },
    },
  });

  // Keep editor content in sync if the parent replaces `value` (e.g. loading
  // an existing post into an editor that has already mounted).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current) editor.commands.setContent(value, false);
  }, [value, editor]);

  if (!editor) return null;

  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="border border-line bg-bone" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 border-b border-line bg-bone-surface px-2 py-2">
        <ToolbarBtn label="Paragraph" onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")}>
          <Pilcrow className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn label="Heading 2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })}>
          <Heading2 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn label="Heading 3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })}>
          <Heading3 className="w-4 h-4" />
        </ToolbarBtn>

        <Divider />

        <ToolbarBtn label="Bold (⌘B)" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")}>
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn label="Italic (⌘I)" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")}>
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn label="Strikethrough" onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")}>
          <Strikethrough className="w-4 h-4" />
        </ToolbarBtn>

        <Divider />

        <ToolbarBtn label="Bulleted list" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")}>
          <List className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn label="Numbered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")}>
          <ListOrdered className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn label="Blockquote" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")}>
          <Quote className="w-4 h-4" />
        </ToolbarBtn>

        <Divider />

        <ToolbarBtn label="Link" onClick={promptLink} active={editor.isActive("link")}>
          <LinkIcon className="w-4 h-4" />
        </ToolbarBtn>

        <Divider />

        <ToolbarBtn label="Undo (⌘Z)" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
          <Undo2 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn label="Redo (⌘⇧Z)" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
          <Redo2 className="w-4 h-4" />
        </ToolbarBtn>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}

function ToolbarBtn({ children, onClick, active, disabled, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={[
        "inline-flex items-center justify-center h-8 w-8 rounded-none transition-colors",
        active ? "bg-forest text-bone" : "text-ink hover:bg-line",
        disabled ? "opacity-30 cursor-not-allowed" : "",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-6 bg-line mx-1" aria-hidden />;
}
